import { useEffect, useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { useGetEnrollmentHYCSVerifyIdentityData, useSubmitEnrollmentHYCSVerifyIdentityData } from '@egw/api'
import { Input, Button, Alert, AlertType, GenericError, RadioGroup } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { EyeIcon } from '@heroicons/react/20/solid'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - VerifyIdentity')

interface FormData {
  ssn: string
  usCitizen: boolean
}

export const VerifyIdentityPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()

  const { data, isLoading, error } = useGetEnrollmentHYCSVerifyIdentityData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSVerifyIdentityData()

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  const [isHide, setIsHide] = useState(true)
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors: formErrors, isValid, touchedFields, dirtyFields },
  } = useForm({
    defaultValues: {
      ssn: '',
      usCitizen: '',
    },
    mode: 'onChange', // Trigger validation on change for real-time validation
  })

  // Protect route
  useValidateRoute(data?.path)

  const ssn = watch('ssn')
  const usCitizen = watch('usCitizen')

  useEffect(() => {
    if (!hasShownIntent && ssn && usCitizen) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_IDENTITY_INTENT, {
        applicationId,
      })
    }
  }, [applicationId, hasShownIntent, ssn, usCitizen])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          ssn: formData.ssn,
          usCitizen: Boolean(formData.usCitizen),
        })
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_IDENTITY_SUBMIT, {
          applicationId,
        })
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_IDENTITY_VERIFIED, {
            applicationId,
          })
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t('hycs.verify-identity.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center"
          >
            <PageHeader title={t('hycs.verify-identity.title')} />
            <p className="typo-primary-sm-regular text-text-meta mb-xl">{t('hycs.verify-identity.description')}</p>

            <Controller
              name="ssn"
              control={control}
              rules={{
                required: t('common.required_field'),
                pattern: {
                  value: /^[0-9]{9}$/,
                  message: t('common.invalid_ssn'),
                },
              }}
              render={({ field: { ref, ...restField } }) => (
                <Input
                  {...restField}
                  label={t('hycs.verify-identity.itin')}
                  maxLength={9}
                  inputMode="numeric"
                  onChange={(e) => {
                    const newValue = e.target.value.replace(/[^0-9]/g, '')
                    restField.onChange(newValue)
                  }}
                  icon={
                    <EyeIcon
                      onClick={() => {
                        setIsHide(!isHide)
                      }}
                      data-testid="ssn_input_icon"
                      className="h-5 w-5 cursor-pointer pointer-events-auto"
                    />
                  }
                  required
                  type={isHide ? 'password' : 'text'}
                  testId="ssn_input"
                  error={touchedFields.ssn ? formErrors.ssn?.message : undefined}
                />
              )}
            />

            <p className="typo-primary-base-semibold mb-md mt-xl">{t('hycs.verify-identity.citizen')}</p>

            <Controller
              name="usCitizen"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  value={restField.value}
                  options={[
                    { value: 1, label: t('common.yes') },
                    { value: 0, label: t('common.no') },
                  ]}
                  name="usCitizen"
                  testId="usCitizen_checkbox"
                />
              )}
            />

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
